import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Search, Handshake } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { toast } from "sonner";

// Fix CJ (2026-09-11): "Vista global de todos los contratos tercerizados, generada sola"
// (Edson, 24/08). Se arma sola con lo cargado en cada obra: contratos de la pestaña Pagos y
// tareas tercerizadas de la pestaña Tareas.
export function TercerizadosGlobalModal({ onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [soloSaldo, setSoloSaldo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/tercerizados`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(err => { toast.error(err.response?.data?.detail || "Error al cargar los tercerizados"); setData({ filas: [] }); });
  }, []);

  const term = q.trim().toLowerCase();
  const filas = (data?.filas || []).filter(f => (!soloSaldo || f.saldo > 0)
    && (!term || `${f.obra} ${f.obra_codigo} ${f.contratista} ${f.ruc} ${(f.tareas || []).join(" ")}`.toLowerCase().includes(term)));
  const suma = (k) => filas.reduce((s, f) => s + (Number(f[k]) || 0), 0);
  const contratistas = new Set(filas.map(f => f.contratista.toLowerCase()).filter(Boolean)).size;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="tercerizados-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><Handshake size={17} className="text-[#EA580C]" /> Tercerizados — todas las obras</h2>
            <p className="text-xs text-gray-500 mt-0.5">Se arma sola con los contratos de la pestaña Pagos y las tareas tercerizadas de cada obra</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-3 flex items-center gap-3 flex-wrap border-b border-gray-100">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar obra, contratista o tarea..." className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm" data-testid="tercerizados-buscar" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input type="checkbox" checked={soloSaldo} onChange={e => setSoloSaldo(e.target.checked)} data-testid="tercerizados-solo-saldo" /> Solo con saldo a pagar
          </label>
          <span className="text-xs text-gray-400">{filas.length} fila(s) · {contratistas} contratista(s)</span>
        </div>
        <div className="overflow-auto flex-1">
          {!data ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div> : (
            <table className="w-full text-sm" data-testid="tercerizados-tabla">
              <thead className="sticky top-0 bg-gray-50 z-10"><tr className="text-xs text-gray-500 uppercase">
                <th className="text-left px-4 py-2.5">Obra</th><th className="text-left px-4 py-2.5">Contratista</th><th className="text-left px-4 py-2.5">Tareas</th>
                <th className="text-right px-4 py-2.5">Monto cerrado</th><th className="text-right px-4 py-2.5">Certificado</th>
                <th className="text-right px-4 py-2.5">Pagado</th><th className="text-right px-4 py-2.5">Saldo</th>
              </tr></thead>
              <tbody>
                {filas.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-gray-400">{(data.filas || []).length ? "Sin coincidencias" : "Todavía no hay tercerizados cargados en ninguna obra"}</td></tr>
                  : filas.map(f => (
                    <tr key={`${f.origen}-${f.id}`} className="border-t border-gray-50 hover:bg-orange-50/40 cursor-pointer" onClick={() => { onClose(); navigate(`/obra/${f.obra_id}`); }} data-testid={`tercerizados-fila-${f.id}`}>
                      <td className="px-4 py-2.5"><p className="font-semibold text-gray-800">{f.obra || "—"}</p><p className="text-[11px] text-gray-400">{f.obra_codigo}</p></td>
                      <td className="px-4 py-2.5"><p className="text-gray-800">{f.contratista || "—"}</p><p className="text-[11px] text-gray-400">{f.origen === "contrato" ? `Contrato${f.ruc ? ` · RUC ${f.ruc}` : ""}` : "Cargado en la tarea"}</p></td>
                      <td className="px-4 py-2.5 text-xs text-gray-600 max-w-[280px]">{(f.tareas || []).join(", ") || "—"}</td>
                      <td className="px-4 py-2.5 text-right">{formatPYG(f.monto_cerrado)}</td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{f.certificado == null ? "—" : formatPYG(f.certificado)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700">{formatPYG(f.pagado)}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${f.saldo > 0 ? "text-[#EA580C]" : f.saldo < 0 ? "text-red-600" : "text-gray-400"}`}>{formatPYG(f.saldo)}</td>
                    </tr>
                  ))}
              </tbody>
              {filas.length > 0 && (
                <tfoot className="sticky bottom-0 bg-orange-50"><tr className="font-bold text-gray-800" data-testid="tercerizados-totales">
                  <td colSpan={3} className="px-4 py-2.5">Totales de lo filtrado</td>
                  <td className="px-4 py-2.5 text-right">{formatPYG(suma("monto_cerrado"))}</td>
                  <td className="px-4 py-2.5 text-right">{formatPYG(suma("certificado"))}</td>
                  <td className="px-4 py-2.5 text-right">{formatPYG(suma("pagado"))}</td>
                  <td className="px-4 py-2.5 text-right" data-testid="tercerizados-total-saldo">{formatPYG(suma("saldo"))}</td>
                </tr></tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
